import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AppIconComponent } from '@components/widgets/icon/app-icon.component';
import { AppUploadActionComponent } from './upload-action.component';

/**
 * BUG-275677 — «no se abre el selector de documentos» en móvil.
 *
 * `nzAccept` declara ahora el tipo MIME ADEMÁS de la extensión. `accept` con solo la extensión es
 * la forma débil en iOS: el sistema la traduce a un tipo uniforme (UTI) y, sin el MIME, hay
 * navegadores que dejan el selector sin ninguna fuente de la que escoger. Declararlos los dos no
 * relaja nada —el filtro de verdad lo hace `beforeUpload`, que rechaza lo que no sea PDF—.
 *
 * NO está comprobado que sea la causa del reporte: con el motor de Safari el selector sí abría
 * con la forma anterior. Es el flanco que se puede cerrar sin tener el dispositivo del reporte.
 */
@Component({
  standalone: true,
  selector: 'app-upload-zone',
  imports: [NzUploadComponent, AppIconComponent],
  template: `
    <nz-upload nzType="drag" nzAccept="application/pdf,.pdf" [nzShowUploadList]="false" [nzBeforeUpload]="beforeUpload">
      <div class="upload-box">
        <div class="upload-icon">
          <app-icon iconName="upload2"></app-icon>
        </div>
        <div class="upload-text">
          Arrastra el archivo aquí o
          <span>Selecciona</span>
        </div>
        <div class="upload-hint">PDF (Máx. 40MB)</div>
      </div>
    </nz-upload>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host ::ng-deep .ant-upload.ant-upload-drag {
        border: 1.5px dashed #d9d9d9;
        border-radius: 12px;
        background: #ffffff;
        padding: 0;
      }

      :host ::ng-deep .ant-upload.ant-upload-drag .ant-upload {
        padding: 0;
      }

      :host ::ng-deep .ant-upload.ant-upload-drag:hover {
        border-color: #9bc5ff;
        background: #f0f7ff;
      }

      .upload-box {
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .upload-icon {
        font-size: 24px;
        color: #bfbfbf;
        margin-bottom: 8px;
      }

      .upload-text {
        font-size: 14px;
        color: #666;
      }

      .upload-text span {
        color: #1677ff;
        font-weight: 500;
        cursor: pointer;
      }

      .upload-hint {
        font-size: 12px;
        color: #999;
        margin-top: 4px;
      }
    `,
  ],
})
export class UploadZoneComponent {
  private parent = inject(AppUploadActionComponent, { optional: true, host: true });
  private message = inject(NzMessageService);

  private static readonly MAX_FILE_SIZE_BYTES = 40 * 1024 * 1024;

  beforeUpload = (file: NzUploadFile): boolean => {
    const nativeFile = (file.originFileObj ?? file) as unknown as File;
    const isPdf = nativeFile.type === 'application/pdf' || nativeFile.name?.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      this.message.error('Solo se permiten archivos PDF');
      return false;
    }
    if (nativeFile.size > UploadZoneComponent.MAX_FILE_SIZE_BYTES) {
      this.message.error('El archivo supera el tamaño máximo permitido de 40MB');
      return false;
    }
    if (this.parent) {
      this.parent.onFileReceived(nativeFile);
    }
    return false;
  };
}
